import { ApiStorageUnitFilter } from 'api/cloudsave/cloudsaveApi';
import { isShared } from 'util/isSharedHelper';
import type { AdvancedFilters, StorageUnit } from './types';
import { Filter, ROOT, StorageUnitStatus, StorageUnitType } from './types';

export const mergeStorageUnits = (
  units: StorageUnit[],
  pending: StorageUnit[],
): StorageUnit[] => {
  if (!pending.length) {
    return units;
  }

  const merged = new Map<string, StorageUnit>();
  units.forEach((unit) => merged.set(unit.id, unit));
  pending.forEach((unit) => merged.set(unit.id, unit));
  return Array.from(merged.values());
};

export const normalizeParentId = (
  value: string | undefined | null,
): string | undefined => (!value || value === ROOT.id ? undefined : value);

export const scopeStorageUnits = (
  units: StorageUnit[],
  parentId?: string,
  type?: StorageUnitType,
) => {
  const requestedParent = normalizeParentId(parentId);
  return units.filter(
    (unit) =>
      !unit.archived &&
      (!type || unit.type === type) &&
      normalizeParentId(unit.parentId) === requestedParent,
  );
};

// Folders, projects and files that inherit metadata from a parent project
// don't carry these fields, so only exclude units whose own value differs.
const matchesAdvancedFilter = (value?: string, selected?: string) =>
  !selected || value === undefined || value === selected;

export const matchesStorageUnitFilters = (
  unit: StorageUnit,
  filters: Filter[],
  advancedFilters?: AdvancedFilters,
) =>
  unit.type === StorageUnitType.Folder ||
  (matchesAdvancedFilter(
    unit.project?.country ?? unit.flowsheetMetadata?.country,
    advancedFilters?.country,
  ) &&
    matchesAdvancedFilter(
      unit.flowsheetMetadata?.application,
      advancedFilters?.application,
    ) &&
    filters.every((filter) => {
      switch (filter) {
        case Filter.LOCKED:
          return !!unit.exclusiveLease;
        case Filter.UNLOCKED:
          return !unit.exclusiveLease;
        case Filter.SHARED:
          return isShared(unit.access);
        case Filter.PRIVATE:
          return unit.type === StorageUnitType.File && !isShared(unit.access);
        case Filter.DRAFT:
          return unit.type === StorageUnitType.File && unit.status.length === 0;
        case Filter.APPROVED:
          return unit.status.includes(StorageUnitStatus.APPROVED);
        default:
          return true;
      }
    }));

export const mapFilterToApiFilter = (
  filter: Filter | undefined,
): ApiStorageUnitFilter | undefined => {
  switch (filter) {
    case Filter.APPROVED:
      return ApiStorageUnitFilter.APPROVED;
    case Filter.DRAFT:
      return ApiStorageUnitFilter.DRAFT;
    case Filter.LAST_MODIFIED:
      return ApiStorageUnitFilter.RECENT;
    case Filter.SHARED:
      return ApiStorageUnitFilter.SHARED;
    case Filter.PRIVATE:
      return ApiStorageUnitFilter.PRIVATE;
    default:
      return undefined;
  }
};
